import { Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import DashboardIcon from '@mui/icons-material/DashboardOutlined'
import { AddIcon, CloseIcon } from './Icons'

const groupWidgets = widgets => {
  const groups = new Map()
  widgets.forEach(widget => {
    const key = widget.category ?? ''
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(widget)
  })
  return [...groups.entries()]
}

function WidgetRow({ widget, added, onAdd }) {
  const { t } = useTranslation()

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        opacity: added ? 0.6 : 1,
        transition: 'background-color 150ms',
        '&:hover': added ? undefined : { bgcolor: 'action.hover' },
      }}
    >
      <DashboardIcon fontSize="small" sx={{ color: added ? 'text.disabled' : 'primary.main', flexShrink: 0 }} />

      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {widget.name}
        </Typography>
        {widget.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {widget.description}
          </Typography>
        )}
      </Box>

      {added ? (
        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
          {t('Added')}
        </Typography>
      ) : (
        <Tooltip title={t('Add to dashboard')}>
          <IconButton size="small" color="primary" onClick={() => onAdd(widget)} sx={{ flexShrink: 0 }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  )
}

export default function WidgetLibrary({ widgets = [], isAdded, onAdd, onClose }) {
  const { t } = useTranslation()
  const groups = groupWidgets(widgets)

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, position: 'sticky', top: 16, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('Widget Library')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('Press add to place a widget on your dashboard.')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ p: 2, maxHeight: '70vh', overflowY: 'auto' }}>
        {widgets.length ? (
          groups.map(([category, items]) => (
            <Box key={category || 'ungrouped'} sx={{ mb: 1 }}>
              {category && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}
                >
                  {category}
                </Typography>
              )}
              {items.map(widget => (
                <WidgetRow key={widget.id} widget={widget} added={isAdded(widget)} onAdd={onAdd} />
              ))}
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.disabled">
            {t('No widgets available for your account.')}
          </Typography>
        )}
      </Box>
    </Paper>
  )
}
